import { useQuery } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';

export interface BackendSubscriptionInfo {
  plan: string;
  status: string;
  stripeSubscription?: {
    id: string;
    status: string;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
  } | null;
}

export interface SubscriptionLimits {
  maxAssets: number;
  maxStorage: number; // in GB
  maxBanks: number;
  maxViewers: number;
}

export interface ExtendedSubscriptionInfo extends BackendSubscriptionInfo {
  isActive: boolean;
  isPro: boolean;
  isPremium: boolean;
  isFree: boolean;
  canAddAssets: boolean;
  limits: SubscriptionLimits;
  isLoading: boolean;
  isFetching: boolean;
}

export function useBackendSubscription(): ExtendedSubscriptionInfo {
  const apiClient = useApiClient();

  const {
    data: subscriptionData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['subscription'],
    queryFn: async (): Promise<BackendSubscriptionInfo> => {
      const response = await apiClient.get('/billing/status');
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute (reduced from 5 minutes for faster updates after subscription changes)
    refetchOnWindowFocus: true, // Refetch when window regains focus
    retry: 3,
  });

  // Get plan limits based on subscription plan
  const getPlanLimits = (plan: string): SubscriptionLimits => {
    switch (plan) {
      case 'pro':
        return {
          maxAssets: 25,
          maxStorage: 10,
          maxBanks: 10,
          maxViewers: 1,
        };
      case 'premium':
        return {
          maxAssets: 100,
          maxStorage: 100,
          maxBanks: 10,
          maxViewers: 3,
        };
      default:
        return {
          maxAssets: 5,
          maxStorage: 1,
          maxBanks: 1,
          maxViewers: 0,
        };
    }
  };

  // Default values while loading or on error
  const defaultData: BackendSubscriptionInfo = {
    plan: 'starter',
    status: 'inactive',
    stripeSubscription: null,
  };

  const data = subscriptionData || defaultData;
  const isPro = data.plan === 'pro';
  const isPremium = data.plan === 'premium';
  const isFree = !isPro && !isPremium;
  const isActive = data.status === 'active';
  const limits = getPlanLimits(data.plan);

  return {
    ...data,
    isActive,
    isPro,
    isPremium,
    isFree,
    canAddAssets: true, // We'll check this against current asset count
    limits,
    isLoading,
    isFetching,
  };
}

// Helper hook to check if user can perform a specific action
export function useCanPerformAction(action: string): boolean {
  const subscription = useBackendSubscription();

  switch (action) {
    case 'add_asset':
      return true; // Limit checked dynamically
    case 'add_bank':
      return true; // Limit checked dynamically
    case 'advanced_analytics':
      return subscription.isPremium;
    case 'priority_support':
      return subscription.isPremium;
    case 'google_drive':
      return subscription.isPro || subscription.isPremium;
    default:
      return true;
  }
}
